// Medical / Lab Portal — My Tickets
//
// Lists every TrustTicket helpdesk record raised by the signed-in
// portal user (scoped by their `client_id`) with a click-to-open
// detail modal. Same `MP` token palette + SectionCard styling as the
// other portal pages so it feels native to the surface.
//
// The data source is the same helpdesk service that powers the
// Raise-Ticket form (src/services/ticketApi.js → /api/helpdesk
// proxy → helpdesk.mytrustlab.co.in). No backend persistence.
//
// Mounts at sidebar id 'my-tickets'. After a successful submit on
// the Raise-Ticket page, MedicalDashboard navigates here so staff
// see their fresh ticket at the top of the list immediately.

import { useEffect, useState, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { MP, MONO, DISPLAY, SectionCard, CardHeader } from './shared';
import { useAuth } from '../../../context/AuthContext';
import { listMyTickets, getTicket } from '../../../services/ticketApi';

// Status → visual tone. Helpdesk only ever surfaces "In progress" or
// "Resolved" per the integration brief; we don't render rawStatus.
const STATUS_TONE = {
  'In progress': { bg: '#FEF3C7', fg: '#92400E', accent: '#F59E0B' },
  Resolved:      { bg: '#D1FAE5', fg: '#065F46', accent: '#10B981' },
};

const formatDateTime = (ms) => {
  if (!ms) return '—';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const timeAgo = (ms) => {
  if (!ms) return '—';
  const diff = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} d ago`;
  const d = new Date(ms);
  return `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`;
};

// ─── Page ────────────────────────────────────────────────────
const MyTickets = ({ onRaise }) => {
  const { user } = useAuth();
  const customerId = user?.client_id;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [openId, setOpenId] = useState(null);

  // Filters — search by title/code/visitor + status tab.
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const refresh = useCallback(async () => {
    if (!customerId) { setLoading(false); return; }
    setLoading(true);
    setErr('');
    try {
      const data = await listMyTickets(customerId);
      setTickets(data?.items || []);
    } catch (e) {
      setErr(e.message || 'Could not load your tickets.');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Filtered list — applies search and status filter.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (q) {
        const blob = `${t.code || ''} ${t.title || ''} ${t.description || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, search, statusFilter]);

  // Counts per status bucket — drives the tab badges.
  const counts = useMemo(() => {
    const c = { all: tickets.length, 'In progress': 0, Resolved: 0 };
    for (const t of tickets) {
      if (c[t.status] !== undefined) c[t.status] += 1;
    }
    return c;
  }, [tickets]);

  // No client_id guard — same shape as the other "missing routing"
  // banners on portal pages so it doesn't feel like a crash.
  if (!loading && !customerId) {
    return (
      <SectionCard sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 14, color: MP.dark, mb: 0.5, fontWeight: 700 }}>
          Account not linked
        </Typography>
        <Typography sx={{ fontSize: 13, color: MP.textMuted }}>
          Your portal account isn't linked to a Client ID yet. Contact admin to enable ticket history.
        </Typography>
      </SectionCard>
    );
  }

  return (
    <>
      {/* Filter / actions strip */}
      <SectionCard sx={{ mb: 1.75 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ p: 2 }}
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code, subject…"
            size="small"
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px', fontSize: 13,
                '& fieldset': { borderColor: MP.border, borderWidth: 1.5 },
                '&:hover fieldset': { borderColor: MP.teal },
              },
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ fontSize: 18, color: MP.textMuted, mr: 1 }} />,
            }}
          />
          <TextField
            select size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{
              minWidth: 160,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px', fontSize: 13,
                '& fieldset': { borderColor: MP.border, borderWidth: 1.5 },
                '&:hover fieldset': { borderColor: MP.teal },
              },
            }}
          >
            <MenuItem value="all">All ({counts.all})</MenuItem>
            <MenuItem value="In progress">In Progress ({counts['In progress']})</MenuItem>
            <MenuItem value="Resolved">Resolved ({counts.Resolved})</MenuItem>
          </TextField>
          <Stack direction="row" spacing={1}>
            {onRaise && (
              <Button
                variant="contained"
                onClick={onRaise}
                startIcon={<AddCircleOutlineIcon />}
                sx={{
                  textTransform: 'none', fontWeight: 700,
                  bgcolor: MP.teal, color: '#fff',
                  '&:hover': { bgcolor: MP.tealDark },
                }}
              >
                New Ticket
              </Button>
            )}
          </Stack>
        </Stack>
      </SectionCard>

      {/* List */}
      {loading ? (
        <Stack spacing={1.25}>
          {[0, 1, 2, 3].map((i) => <RowSkeleton key={i} i={i} />)}
        </Stack>
      ) : err ? (
        <SectionCard sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: MP.red || '#B91C1C', mb: 1.5 }}>{err}</Typography>
          <Button onClick={refresh} size="small" variant="outlined">Retry</Button>
        </SectionCard>
      ) : !filtered.length ? (
        <SectionCard sx={{ p: 5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: MP.dark, mb: 0.75 }}>
            {tickets.length === 0 ? 'No tickets yet' : 'No tickets match your filters'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: MP.textMuted, mb: 2 }}>
            {tickets.length === 0
              ? 'Tickets you raise via the portal will appear here.'
              : 'Try clearing the search or the status filter.'}
          </Typography>
          {tickets.length === 0 && onRaise && (
            <Button
              variant="contained"
              onClick={onRaise}
              startIcon={<AddCircleOutlineIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700,
                bgcolor: MP.teal, color: '#fff',
                '&:hover': { bgcolor: MP.tealDark },
              }}
            >
              Raise your first ticket
            </Button>
          )}
        </SectionCard>
      ) : (
        <SectionCard>
          <CardHeader
            title="My Tickets"
            subtitle={`${filtered.length} ${filtered.length === 1 ? 'ticket' : 'tickets'} shown`}
          />
          <Stack spacing={1.25} sx={{ p: '15px' }}>
            {filtered.map((t) => (
              <TicketRow key={t.id} ticket={t} onClick={() => setOpenId(t.id)} />
            ))}
          </Stack>
        </SectionCard>
      )}

      {/* Detail modal — owns its own fetch + polling */}
      <TicketDetailDialog
        open={!!openId}
        ticketId={openId}
        customerId={customerId}
        onClose={() => setOpenId(null)}
      />
    </>
  );
};

// ─── List row ────────────────────────────────────────────────
const TicketRow = ({ ticket, onClick }) => {
  const tone = STATUS_TONE[ticket.status] || STATUS_TONE['In progress'];
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        bgcolor: MP.white,
        border: `1px solid ${MP.border}`,
        borderRadius: '6px',
        boxShadow: MP.shadow,
        px: 2,
        py: 1.75,
        pl: 2.5,
        cursor: 'pointer',
        transition: 'all .15s',
        overflow: 'hidden',
        // Left accent stripe — open vs resolved at a glance.
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0, top: 0, bottom: 0,
          width: 4,
          bgcolor: tone.accent,
        },
        '&:hover': {
          borderColor: MP.teal,
          boxShadow: '0 6px 18px rgba(15,32,25,0.06)',
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 0.75 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{
            fontFamily: MONO, fontSize: 10.5, fontWeight: 700,
            color: MP.tealDark, letterSpacing: '0.06em', mb: 0.25,
          }}>
            {ticket.code || '—'}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: MP.dark, lineHeight: 1.35 }} noWrap>
            {ticket.title || '(No subject)'}
          </Typography>
        </Box>
        <StatusPill tone={tone} label={ticket.status} />
      </Stack>

      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" rowGap={0.5} sx={{ fontSize: 11.5, color: MP.textMuted }}>
        <Box component="span">Raised {timeAgo(ticket.raisedAt)}</Box>
        {ticket.status === 'Resolved' && ticket.closedAt && (
          <>
            <Box component="span" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: MP.border }} />
            <Box component="span" sx={{ color: tone.fg, fontWeight: 600 }}>
              Resolved {timeAgo(ticket.closedAt)}
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};

// ─── Skeleton row for loading state ──────────────────────────
// Mirrors TicketRow exactly - same padding, same left stripe, same gaps and
// the same two text rows. A placeholder that is not the shape of the thing
// it stands in for makes the page jump when the data lands, which is the
// part that actually looks unfinished.
//
// `i` staggers the pulse down the list so the rows breathe in sequence
// rather than flashing in unison.
const RowSkeleton = ({ i = 0 }) => (
  <Box
    sx={{
      position: 'relative',
      bgcolor: MP.white,
      border: `1px solid ${MP.border}`,
      borderRadius: '6px',
      boxShadow: MP.shadow,
      px: 2,
      py: 1.75,
      pl: 2.5,
      overflow: 'hidden',
      // The stripe TicketRow carries, in a neutral tint until we know the
      // ticket's status and therefore its colour.
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0, top: 0, bottom: 0,
        width: 4,
        bgcolor: MP.borderSoft,
      },
      '& .MuiSkeleton-root': { animationDelay: `${i * 90}ms` },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 0.75 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Skeleton variant="text" width={78} height={11} sx={{ mb: 0.25 }} />
        <Skeleton variant="text" width="52%" height={18} />
      </Box>
      <Skeleton variant="rounded" width={86} height={20} sx={{ borderRadius: '20px', flexShrink: 0 }} />
    </Stack>
    <Skeleton variant="text" width={132} height={12} />
  </Box>
);

// ─── Status pill ────────────────────────────────────────────
const StatusPill = ({ tone, label }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex', alignItems: 'center', flexShrink: 0,
      bgcolor: tone?.bg || MP.bg, color: tone?.fg || MP.textMuted,
      fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em',
      px: 1.25, py: 0.5, borderRadius: 100, whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

// ─── Detail modal — opens on row click, polls every 30 s ────
const TicketDetailDialog = ({ open, ticketId, customerId, onClose }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const refresh = useCallback(async () => {
    if (!ticketId || !customerId) return;
    setLoading(true);
    setErr('');
    try {
      const data = await getTicket(ticketId, customerId);
      setTicket(data);
    } catch (e) {
      setErr(e.message || 'Could not load this ticket.');
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [ticketId, customerId]);

  useEffect(() => {
    if (open && ticketId) refresh();
    else setTicket(null);
  }, [open, ticketId, refresh]);

  // NO POLLING. This used to re-fetch the open ticket every 30 seconds so a
  // CSD closure appeared without the user doing anything. It is gone by
  // request: nothing in this app calls the API on a timer any more.
  //
  // TRADE-OFF, so nobody is surprised by it: if support resolves a ticket
  // while the dialog is open, the status no longer changes by itself. It is
  // correct again as soon as the dialog is reopened, since opening fetches.

  const tone = ticket?.status ? STATUS_TONE[ticket.status] : null;
  const resolved = ticket?.status === 'Resolved';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '6px', overflow: 'hidden' } }}
    >
      {/* Status-tinted header band */}
      <Box sx={{
        position: 'relative',
        background: tone
          ? `linear-gradient(135deg, ${tone.bg} 0%, ${MP.white} 80%)`
          : `linear-gradient(135deg, ${MP.tealLight} 0%, ${MP.white} 80%)`,
        borderBottom: `1px solid ${MP.border}`,
        px: 3, pt: 2.75, pb: 2.25,
      }}>
        {ticket && (
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
            <Typography sx={{
              fontFamily: MONO, fontSize: 11.5, fontWeight: 700,
              color: MP.tealDark, letterSpacing: '0.08em',
            }}>
              {ticket.code}
            </Typography>
            {tone && <StatusPill tone={tone} label={ticket.status} />}
          </Stack>
        )}
        {ticket && (
          <Typography sx={{
            fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', color: MP.dark, lineHeight: 1.3,
          }}>
            {ticket.title}
          </Typography>
        )}
        {ticket && (
          <Typography sx={{ fontSize: 12, color: MP.textMuted, mt: 0.5 }}>
            Raised on {formatDateTime(ticket.raisedAt)}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {loading && !ticket && (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={24} sx={{ color: MP.teal }} />
            <Typography sx={{ fontSize: 12, color: MP.textMuted, mt: 1.5 }}>Loading ticket…</Typography>
          </Stack>
        )}
        {err && !ticket && (
          <Typography sx={{ fontSize: 13, color: MP.red || '#B91C1C', textAlign: 'center', py: 3 }}>
            {err}
          </Typography>
        )}

        {ticket && (
          <Stack spacing={2}>
            {/* Status card — pinned above the description so the answer
                comes before the receipt. */}
            {!resolved && (
              <Box sx={{
                bgcolor: '#FFFBEB', border: '1px solid #FDE68A',
                borderRadius: '6px', p: 2,
                display: 'flex', gap: 1.25, alignItems: 'flex-start',
              }}>
                <HourglassEmptyOutlinedIcon sx={{ color: '#92400E', fontSize: 22, flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#78350F' }}>
                    We're working on it
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#92400E', mt: 0.5, lineHeight: 1.55 }}>
                    Customer support will update you here when the ticket is resolved.
                  </Typography>
                </Box>
              </Box>
            )}

            {resolved && (
              <Box sx={{
                bgcolor: '#ECFDF5', border: '1px solid #A7F3D0',
                borderRadius: '6px', p: 2,
                display: 'flex', gap: 1.25, alignItems: 'flex-start',
              }}>
                <CheckCircleOutlineIcon sx={{ color: '#065F46', fontSize: 22, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#065F46' }}>
                    Resolved on {formatDateTime(ticket.closedAt)}
                  </Typography>
                  {ticket.resolution ? (
                    <Typography sx={{ fontSize: 13, color: '#064E3B', whiteSpace: 'pre-wrap', mt: 0.75, lineHeight: 1.55 }}>
                      {ticket.resolution}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 12, color: '#065F46', fontStyle: 'italic', mt: 0.5 }}>
                      No resolution note was attached.
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Original description echo */}
            {ticket.description && (
              <Box sx={{
                bgcolor: MP.bg, border: `1px solid ${MP.border}`,
                borderRadius: '6px', p: 2,
              }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MP.textMuted, letterSpacing: '0.1em', mb: 0.75 }}>
                  YOUR DESCRIPTION
                </Typography>
                <Typography sx={{ fontSize: 13, color: MP.dark, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                  {ticket.description}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      <Box sx={{
        display: 'flex', alignItems: 'center',
        px: 2.5, py: 1.5,
        borderTop: `1px solid ${MP.border}`,
      }}>
        {!resolved && ticket && (
          <Typography sx={{ fontSize: 11, color: MP.textMuted, fontStyle: 'italic', mr: 'auto' }}>
            Auto-refreshes every 30 seconds
          </Typography>
        )}
        <Box sx={{ ml: 'auto' }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: MP.textMuted }}>
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MyTickets;
